import { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2, Save, Trash2, Megaphone, Sun, Plane, CalendarOff, Send, Users, UserCog, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const KIND_OPTIONS = [
  { v: "summer", l: "🌴 Periodo estivo", icon: Sun },
  { v: "holiday", l: "🎄 Festività", icon: Plane },
  { v: "temporary", l: "🔒 Chiusura temporanea", icon: CalendarOff },
  { v: "custom", l: "📣 Comunicazione personalizzata", icon: Megaphone },
];
const AUDIENCE_OPTIONS = [
  { v: "clients", l: "Solo clienti", icon: Users, cls: "bg-blue-100 text-blue-700" },
  { v: "staff", l: "Solo collaboratori", icon: UserCog, cls: "bg-amber-100 text-amber-800" },
  { v: "both", l: "Tutti (clienti + staff)", icon: Megaphone, cls: "bg-emerald-100 text-emerald-700" },
];

const fmt = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AgencyClosures() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency/closures");
      setItems(data || []);
    } catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    const now = new Date();
    const end = new Date(); end.setDate(now.getDate() + 14); end.setHours(18, 0, 0, 0);
    setEdit({
      id: null, title: "Chiusura agenzia",
      message: "", kind: "summer",
      start_at: fmt(now), end_at: fmt(end),
      hours: "", audience: "both",
      is_active: true,
      show_banner_dashboard: true,
      show_banner_public: true,
    });
  };
  const openEdit = (cl) => setEdit({
    ...cl,
    start_at: fmt(new Date(cl.start_at)), end_at: fmt(new Date(cl.end_at)),
  });

  const save = async () => {
    if (!edit.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = {
        ...edit,
        start_at: new Date(edit.start_at).toISOString(),
        end_at: new Date(edit.end_at).toISOString(),
      };
      if (edit.id) await api.put(`/agency/closures/${edit.id}`, payload);
      else await api.post("/agency/closures", payload);
      toast.success(edit.id ? "Chiusura aggiornata" : "Chiusura creata");
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!edit?.id) return;
    if (!window.confirm(`Eliminare "${edit.title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/agency/closures/${edit.id}`);
      toast.success("Chiusura eliminata");
      setEdit(null); load();
    } catch (e) { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const notify = async (cl) => {
    const auds = AUDIENCE_OPTIONS.find((a) => a.v === cl.audience)?.l || cl.audience;
    if (!window.confirm(`Inviare il messaggio "${cl.title}" a "${auds}"? Riceveranno email${cl.audience !== "clients" ? " + push" : ""}.`)) return;
    try {
      const { data } = await api.post(`/agency/closures/${cl.id}/notify`);
      toast.success(`Inviato a ${data.sent} destinatari (${data.audience})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
  };

  return (
    <div className="space-y-5" data-testid="closures-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-rose-600 mb-1">Comunicazioni</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <Megaphone className="h-7 w-7 text-rose-600" /> Chiusure agenzia
          </h1>
          <p className="text-sm text-slate-500 mt-1">Avvisi di chiusura estiva, festività o periodi temporanei con orari speciali. Audience selezionabile (clienti, collaboratori o entrambi).</p>
        </div>
        <Button onClick={openCreate} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="closures-create">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova chiusura
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-slate-500">
          Nessuna chiusura programmata. Creane una per comunicare ferie, festività o chiusure temporanee.
        </CardContent></Card>
      ) : (
        <div className="space-y-2.5">
          {items.map((cl) => {
            const aud = AUDIENCE_OPTIONS.find((a) => a.v === cl.audience) || AUDIENCE_OPTIONS[2];
            const Kind = KIND_OPTIONS.find((k) => k.v === cl.kind)?.icon || Megaphone;
            const now = new Date();
            const start = new Date(cl.start_at), end = new Date(cl.end_at);
            const active = cl.is_active && now >= start && now <= end;
            const upcoming = cl.is_active && now < start;
            const expired = now > end;
            return (
              <Card key={cl.id} className={`${active ? "border-emerald-300 bg-emerald-50/30" : expired ? "opacity-60" : ""}`} data-testid={`closure-${cl.id}`}>
                <CardContent className="py-3 flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center ${aud.cls} shrink-0`}>
                    <Kind className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <h3 className="font-medium text-sm flex items-center gap-2">
                        {cl.title}
                        {active && <span className="text-[10px] uppercase font-semibold tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">ATTIVA</span>}
                        {upcoming && <span className="text-[10px] uppercase font-semibold tracking-wider text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">IN ARRIVO</span>}
                        {expired && <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">SCADUTA</span>}
                      </h3>
                      <div className="text-[11px] text-slate-500 tabular-nums">{formatDateTime(cl.start_at)} → {formatDateTime(cl.end_at)}</div>
                    </div>
                    {cl.message && <p className="text-xs text-slate-600 mt-0.5">{cl.message}</p>}
                    {cl.hours && <p className="text-xs text-amber-700 mt-0.5">⏱ {cl.hours}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${aud.cls}`}>{aud.l}</span>
                      {cl.show_banner_dashboard && <span className="text-[10px] text-slate-500">📌 Banner interno</span>}
                      {cl.show_banner_public && <span className="text-[10px] text-slate-500">🌍 Banner pubblico</span>}
                      {cl.notify_sent_at && <span className="text-[10px] text-emerald-700">✓ Inviato a {cl.notify_sent_count} il {formatDateTime(cl.notify_sent_at)}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(cl)} className="h-7 text-xs">Modifica</Button>
                    <Button size="sm" onClick={() => notify(cl)} disabled={!cl.is_active} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid={`closure-notify-${cl.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Manda ora
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="closure-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica chiusura" : "Nuova chiusura"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-2.5">
              <div>
                <Label className="text-xs">Titolo *</Label>
                <Input data-testid="closure-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} autoFocus />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <select className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value })} data-testid="closure-kind">
                  {KIND_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5"><AlertCircle className="h-3 w-3 text-amber-600" /> A chi è destinato il messaggio</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-1">
                  {AUDIENCE_OPTIONS.map((o) => {
                    const Icon = o.icon;
                    return (
                      <button key={o.v} type="button" onClick={() => setEdit({ ...edit, audience: o.v })} className={`p-2 rounded border text-[11px] flex flex-col items-center gap-1 ${edit.audience === o.v ? `${o.cls} border-current` : "border-slate-200 hover:bg-slate-50"}`} data-testid={`closure-audience-${o.v}`}>
                        <Icon className="h-4 w-4" />{o.l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Dal *</Label>
                  <Input data-testid="closure-start" type="datetime-local" value={edit.start_at} onChange={(e) => setEdit({ ...edit, start_at: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Al *</Label>
                  <Input data-testid="closure-end" type="datetime-local" value={edit.end_at} onChange={(e) => setEdit({ ...edit, end_at: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Orari speciali (opzionale)</Label>
                <Input data-testid="closure-hours" value={edit.hours} onChange={(e) => setEdit({ ...edit, hours: e.target.value })} placeholder='Es. "Lun-Ven 9:00-13:00, chiuso il pomeriggio"' />
              </div>
              <div>
                <Label className="text-xs">Messaggio (opzionale)</Label>
                <Textarea data-testid="closure-message" rows={3} value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} placeholder="Es. L'agenzia resterà chiusa per ferie. Per urgenze..." />
              </div>
              <div className="space-y-1.5 border-t pt-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} className="h-3.5 w-3.5" data-testid="closure-active" />
                  <span>Chiusura attiva (banner + invio abilitati)</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={edit.show_banner_dashboard} onChange={(e) => setEdit({ ...edit, show_banner_dashboard: e.target.checked })} className="h-3.5 w-3.5" data-testid="closure-banner-dash" />
                  <span>Mostra banner sulla dashboard interna (staff)</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={edit.show_banner_public} onChange={(e) => setEdit({ ...edit, show_banner_public: e.target.checked })} className="h-3.5 w-3.5" data-testid="closure-banner-pub" />
                  <span>Mostra banner sulla pagina pubblica preventivi (clienti)</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {edit?.id && <Button variant="outline" onClick={remove} disabled={busy} className="text-rose-600 border-rose-300 mr-auto" data-testid="closure-delete"><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button>}
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={save} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="closure-save">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
